import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import {
  ArrowLeft, Plus, Search, Check, Circle, Trash2, ClipboardList,
  Image as ImageIcon, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, MessageCircle,
} from 'lucide-react';
import dayjs from 'dayjs';
import { PAPER_BASE } from '../config';
import { authSelector } from '../../../features/auth/slice';

export default function PaperAdminTasksPage() {
  const { boardId } = useParams();
  const navigate = useNavigate();
  const { me } = useSelector(authSelector.selectAuthState);
  const isSuperAdmin = me?.superAdminYn === 'Y';

  const [board, setBoard] = useState(null);
  const [data, setData] = useState({ tasks: [], total: 0, page: 1, totalPages: 1 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const load = useCallback(() => {
    axios.get(`/api/paper-task/boards/${boardId}/tasks?page=${page}&limit=20&search=${search}`).then(r => setData(r.data));
  }, [boardId, page, search]);

  useEffect(() => {
    axios.get('/api/paper-task/boards').then(r => { const b = r.data.find(x => x.id === parseInt(boardId)); setBoard(b); });
    load();
  }, [boardId, load]);

  const toggle = async (taskId) => {
    await axios.patch(`/api/paper-task/boards/${boardId}/tasks/${taskId}/toggle`);
    load();
  };
  const remove = async (taskId) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    await axios.delete(`/api/paper-task/boards/${boardId}/tasks/${taskId}`);
    load();
  };
  const doSearch = () => { setPage(1); setSearch(searchInput); };

  const createAndOpen = async () => {
    const r = await axios.post(`/api/paper-task/boards/${boardId}/tasks`, { title: '새 할일', content: '' });
    navigate(`${PAPER_BASE}/admin/tasks/${boardId}/${r.data.id}`);
  };

  const moveTask = async (index, dir) => {
    const tasks = [...data.tasks];
    const target = index + dir;
    if (target < 0 || target >= tasks.length) return;
    [tasks[index], tasks[target]] = [tasks[target], tasks[index]];
    const orders = tasks.map((t, i) => ({ id: t.id, sortOrder: i }));
    setData(d => ({ ...d, tasks }));
    await axios.patch(`/api/paper-task/boards/${boardId}/tasks/reorder`, { orders });
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate(`${PAPER_BASE}/admin/tasks`)} className="p-1.5 text-white/30 hover:text-white rounded-lg hover:bg-white/5">
            <ArrowLeft size={18} />
          </button>
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-white truncate">{board?.name || '게시판'}</h1>
            {board?.description && <p className="text-white/40 text-sm truncate">{board.description}</p>}
          </div>
        </div>
        <button onClick={createAndOpen}
          className="flex items-center gap-1.5 bg-violet-500 text-white text-sm px-4 py-2 rounded-lg hover:bg-violet-400 transition-colors flex-shrink-0">
          <Plus size={16} /> 추가
        </button>
      </div>

      <div className="flex gap-2 mb-4">
        <div className="flex-1 flex items-center bg-white/[0.03] border border-white/[0.06] rounded-lg px-3">
          <Search size={14} className="text-white/30" />
          <input value={searchInput} onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && doSearch()}
            placeholder="제목 검색..." className="flex-1 bg-transparent text-white text-sm py-2 px-2 focus:outline-none placeholder-white/30" />
        </div>
        <button onClick={doSearch} className="px-4 py-2 bg-white/5 text-white/60 text-sm rounded-lg hover:bg-white/10 transition-colors">검색</button>
      </div>

      <div className="space-y-1.5">
        {data.tasks.map((t, idx) => (
          <div key={t.id}
            className={`flex items-center gap-3 bg-white/[0.03] border rounded-lg px-4 py-3 group transition-all ${
              t.completed ? 'border-white/[0.03] opacity-50' : 'border-white/[0.06] hover:border-violet-400/30'}`}>
            {isSuperAdmin && (
              <div className="flex flex-col gap-0.5 flex-shrink-0">
                <button onClick={() => moveTask(idx, -1)} disabled={idx === 0}
                  className="p-0.5 text-white/20 hover:text-white disabled:opacity-20"><ChevronUp size={12} /></button>
                <button onClick={() => moveTask(idx, 1)} disabled={idx === data.tasks.length - 1}
                  className="p-0.5 text-white/20 hover:text-white disabled:opacity-20"><ChevronDown size={12} /></button>
              </div>
            )}

            <button onClick={() => toggle(t.id)} className="flex-shrink-0">
              {t.completed
                ? <Check size={18} className="text-green-400" />
                : <Circle size={18} className="text-white/20 hover:text-violet-400" />}
            </button>

            <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`${PAPER_BASE}/admin/tasks/${boardId}/${t.id}`)}>
              <p className={`text-sm font-medium truncate ${t.completed ? 'text-white/30 line-through' : 'text-white'}`}>{t.title}</p>
              <div className="flex items-center gap-2 text-xs text-white/30 mt-0.5">
                <span>{t.memberAuthor?.nickName || t.memberAuthor?.name || t.author?.name}</span>
                <span>{dayjs(t.createdAt).format('MM.DD HH:mm')}</span>
                {t.images && t.images.length > 0 && <span className="flex items-center gap-0.5"><ImageIcon size={10} />{t.images.length}</span>}
                {t.comments && t.comments.length > 0 && <span className="flex items-center gap-0.5"><MessageCircle size={10} />{t.comments.length}</span>}
                {t.completed && t.completer && <span className="text-green-400/60">{t.completer.name} 완료</span>}
              </div>
            </div>

            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
              {(t.memberAuthorId === me?.id || t.authorId === me?.id || isSuperAdmin) && (
                <button onClick={() => remove(t.id)} className="p-1 text-white/30 hover:text-red-400"><Trash2 size={14} /></button>
              )}
            </div>
          </div>
        ))}
        {data.tasks.length === 0 && (
          <div className="text-center py-16">
            <ClipboardList size={32} className="text-white/10 mx-auto mb-2" />
            <p className="text-white/30 text-sm">{search ? '검색 결과가 없습니다.' : '할일이 없습니다.'}</p>
          </div>
        )}
      </div>

      {data.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
            className="p-2 text-white/40 hover:text-white disabled:opacity-30"><ChevronLeft size={16} /></button>
          {Array.from({ length: data.totalPages }, (_, i) => i + 1)
            .filter(p => Math.abs(p - page) <= 2 || p === 1 || p === data.totalPages)
            .map((p, i, arr) => (
              <span key={p}>
                {i > 0 && arr[i - 1] !== p - 1 && <span className="text-white/20 px-1">…</span>}
                <button onClick={() => setPage(p)}
                  className={`w-8 h-8 rounded-lg text-sm ${p === page ? 'bg-violet-500 text-white' : 'text-white/40 hover:text-white hover:bg-white/5'}`}>
                  {p}
                </button>
              </span>
            ))}
          <button onClick={() => setPage(p => Math.min(data.totalPages, p + 1))} disabled={page >= data.totalPages}
            className="p-2 text-white/40 hover:text-white disabled:opacity-30"><ChevronRight size={16} /></button>
        </div>
      )}
    </div>
  );
}
